/**
 * Presentation helpers for the deterministic clinical facts the backend now
 * computes (guideline staging + prognostic indices) and stamps into a summary /
 * organ-score `structured_fields`. These are DISPLAY-ONLY: every value arrives
 * already computed by a validated instrument (KDIGO/GOLD band tables, FIB-4/MELD/
 * KFRE) — this module only maps a stage/category onto the shared severity ramp
 * (`OrganBand`) so staging, prognosis and organ scores read in one visual
 * language, with a glyph + label so colour is never the sole cue.
 */

import type { OrganBand } from './organHealth';

// ---------------------------------------------------------------- guideline staging
export interface GuidelineStage {
  /** The metric that was staged, e.g. "egfr" / "fev1_pct". */
  concept: string;
  /** The stage label as computed, e.g. "G4", "G3a A2", "GOLD 3". */
  stage: string;
  /** Published basis, e.g. "KDIGO (CKD-EPI 2021)" / "GOLD". */
  guideline: string;
  /** What the stage IS, e.g. "KDIGO GFR category" (may be absent on old rows). */
  label?: string;
}

// KDIGO GFR category severity (a single eGFR is a GFR category, not a diagnosis).
const KDIGO_BAND: Record<string, OrganBand> = {
  G1: 'healthy', G2: 'mild', G3a: 'moderate', G3b: 'moderate', G4: 'severe', G5: 'critical',
};
// GOLD spirometric grade severity.
const GOLD_BAND: Record<string, OrganBand> = {
  'GOLD 1': 'mild', 'GOLD 2': 'moderate', 'GOLD 3': 'severe', 'GOLD 4': 'critical',
};

/** Map a computed stage label onto the shared severity ramp. Unknown/unstageable
 * -> 'unknown' (neutral), never a guessed severity. */
export function stageBand(stage: string): OrganBand {
  const s = (stage || '').trim();
  const g = s.match(/G(\d)([ab])?/i);
  if (g) {
    const key = `G${g[1]}${(g[2] || '').toLowerCase()}`;
    if (KDIGO_BAND[key]) return KDIGO_BAND[key];
  }
  const gold = Object.keys(GOLD_BAND).find((k) => s.toUpperCase().startsWith(k.toUpperCase()));
  if (gold) return GOLD_BAND[gold];
  return 'unknown';
}

// ---------------------------------------------------------------- prognostic indices
export interface PrognosticIndex {
  /** Calculator key: 'fib4' | 'meld' | 'kfre'. */
  name: string;
  value: number | string | null;
  category: string | null; // low | indeterminate | moderate | high
  units?: string;
  horizon_years?: number;
  provisional?: boolean;
}

/** Clinical label + one-line meaning per calculator, mirroring the backend's
 * organ_health_context._PROGRESSION_META so the two never drift. */
export const PROGNOSTIC_META: Record<string, { label: string; meaning: string }> = {
  fib4: { label: 'FIB-4', meaning: 'Fibrosis-4 index — advanced-fibrosis likelihood (Sterling/EASL)' },
  meld: { label: 'MELD', meaning: 'Model for End-Stage Liver Disease — 90-day mortality / transplant priority (UNOS)' },
  kfre: { label: 'KFRE', meaning: 'Kidney Failure Risk Equation — kidney-failure risk (Tangri 4-variable)' },
};

const CATEGORY_BAND: Record<string, OrganBand> = {
  low: 'healthy',
  mild: 'mild',
  indeterminate: 'moderate',
  moderate: 'moderate',
  high: 'severe',
  critical: 'critical',
};

/** Map a calculator category onto the severity ramp. */
export function categoryBand(category: string | null | undefined): OrganBand {
  return CATEGORY_BAND[(category || '').trim().toLowerCase()] ?? 'unknown';
}

// Static, safelist-safe chip classes per band (never build `bg-band-${x}`
// dynamically — Tailwind would drop it). Small tinted pill: -bg tint + -text
// foreground + a hairline border, matching the token system's chip treatment.
export const BAND_CHIP: Record<OrganBand, string> = {
  healthy: 'bg-band-normal-bg text-band-normal-text border-band-normal/30',
  mild: 'bg-band-mild-bg text-band-mild-text border-band-mild/30',
  moderate: 'bg-band-moderate-bg text-band-moderate-text border-band-moderate/40',
  severe: 'bg-band-severe-bg text-band-severe-text border-band-severe/40',
  critical: 'bg-band-critical-bg text-band-critical-text border-band-critical/50',
  unknown: 'bg-surface-sunken text-text-tertiary border-border',
};

/** Safely pull an array of a shape out of a parsed `structured_fields` value. */
export function readStructuredList<T>(structured: unknown, key: string): T[] {
  if (structured && typeof structured === 'object') {
    const v = (structured as Record<string, unknown>)[key];
    if (Array.isArray(v)) return v as T[];
  }
  return [];
}

/** Normalise `structured_fields.progression` ({fib4:{...}} | absent) to a list. */
export function readProgression(structured: unknown): PrognosticIndex[] {
  if (!structured || typeof structured !== 'object') return [];
  const prog = (structured as Record<string, unknown>).progression;
  if (!prog || typeof prog !== 'object') return [];
  return Object.entries(prog as Record<string, Record<string, unknown>>).map(([name, d]) => ({
    name,
    value: (d?.value as number | string | null) ?? null,
    category: (d?.category as string | null) ?? null,
    units: d?.units as string | undefined,
    horizon_years: d?.horizon_years as number | undefined,
    provisional: Boolean(d?.provisional),
  }));
}
